import {
  BreadcrumbsItem,
  ComponentBody,
  Container,
  PageHeader,
  Section,
} from '../components/ui';
import { Seo } from '../components/layout';
import {
  getNetworkStatusDetails,
  initializeApolloClient,
} from '@wwtr/libs/apollo';
import PAGE_QUERY from '@wwtr/gql/queries/page.gql';
import PAGES_INDEX_QUERY from '@wwtr/gql/queries/pages-index.gql';
import { minutesToSeconds } from 'date-fns';
import React from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { PageQuery, Pages_Pages_Entry } from '@wwtr/generated';
import App404 from '@wwtr/pages/404';

const ContentPage = () => {
  const router = useRouter();
  const { uri } = router.query;

  const {
    data: pageData,
    networkStatus: pageNetworkStatus,
    error: pageError,
  } = useQuery<PageQuery>(PAGE_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      uri: uri,
    },
    skip: !uri,
  });

  const { isLoading, isReady } = getNetworkStatusDetails(pageNetworkStatus);

  if (isReady && (pageError || !pageData?.entry)) {
    return <App404 />;
  }

  const entry = pageData?.entry as Pages_Pages_Entry;

  let breadCrumbs: BreadcrumbsItem[] = [];

  if (isReady) {
    breadCrumbs.push({
      label: entry?.title,
      shorten: true,
    });
  }

  return (
    <>
      <Seo title={entry?.title} />
      <PageHeader
        title={entry?.title}
        pageTitle={entry?.title}
        isLoading={isLoading}
        breadCrumbs={breadCrumbs}
      />
      <Section>
        <Container>
          <article>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-8 xl:col-span-7">
                <div
                  className="mb-8 text-2xl leading-snug lg:text-3xl lg:leading-snug text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: entry?.lead,
                  }}
                />
                <ComponentBody body={entry?.body} />
              </div>
            </div>
          </article>
        </Container>
      </Section>
    </>
  );
};

export default ContentPage;

export async function getStaticPaths() {
  const apolloClient = initializeApolloClient();

  const { data, errors } = await apolloClient.query({
    query: PAGES_INDEX_QUERY,
  });

  if (errors) {
    console.log('Error in query in getStaticPaths, ', errors);
  }

  const paths = data.entries.map(entry => {
    return {
      params: {
        uri: [entry.uri],
      },
    };
  });

  return {
    paths,
    fallback: false, // Render all on build time, return 404 if a path outside this list is requested
  };
}

export async function getStaticProps({ params }) {
  const apolloClient = initializeApolloClient();

  const { errors } = await apolloClient.query({
    query: PAGE_QUERY,
    variables: {
      uri: params.uri,
    },
  });

  if (errors) {
    console.log('Error in getStaticProps', errors);
  }

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
    },
    revalidate: minutesToSeconds(5),
  };
}
